import { ArrowRight, CircleCheck, File as FileIcon, TriangleAlert, Upload } from "lucide-react";
import { useRef, useState } from "react";

import { Eyebrow } from "@/components/eyebrow";
import { Field } from "@/components/field";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { type AnswerSuggestion, type PrefillResponse, usePrefill } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import type { CompanyMeta } from "./types";

interface PrefillStepProps {
  meta: CompanyMeta;
  suggestionCount: number;
  onApply: (result: { summary: string; suggestions: AnswerSuggestion[] }) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function PrefillStep({
  meta,
  suggestionCount,
  onApply,
  onBack,
  onContinue,
}: PrefillStepProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [pasted, setPasted] = useState("");
  const [useWebsite, setUseWebsite] = useState(false);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PrefillResponse | null>(null);

  const prefillMutation = usePrefill();
  const analyzing = prefillMutation.isPending;

  function addFiles(list: FileList | null) {
    if (!list) {
      return;
    }
    setFiles((f) => [...f, ...Array.from(list)].slice(0, 6));
  }
  function removeFile(index: number) {
    setFiles((f) => f.filter((_, i) => i !== index));
  }

  const hasInput =
    files.length > 0 || pasted.trim().length > 0 || (useWebsite && !!meta.company_url);

  async function analyze() {
    setError(null);
    try {
      const data = await prefillMutation.mutateAsync({
        files,
        pastedText: pasted.trim() || undefined,
        url: useWebsite && meta.company_url ? meta.company_url : undefined,
      });
      if (!data) {
        throw new Error("Analysis failed.");
      }
      setResult(data);
      onApply({ summary: data.summary, suggestions: data.suggestions });
    } catch (err) {
      logger.debug("[assess] prefill analysis failed:", err);
      const status = (err as { status?: number } | undefined)?.status;
      if (status === 429) {
        setError("Too many prefill attempts. Wait a minute and try again.");
        return;
      }
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      setError(typeof detail === "string" ? detail : "Analysis failed.");
    }
  }

  return (
    <div>
      <Eyebrow>Step 2 of 4 · Prefill</Eyebrow>
      <h1 className="text-primary mt-3 max-w-[18ch] text-4xl font-normal tracking-tight sm:text-5xl">
        Drop in your materials and <span className="text-accent">we’ll prefill the questions.</span>
      </h1>
      <p className="text-muted-foreground mt-4 mb-6 text-lg">
        Strategy decks, annual reports, product docs, or just paste context. We read them and
        suggest answers you can review and edit. Optional; you can skip and answer everything
        yourself.
      </p>

      {error && (
        <div className="border-destructive bg-destructive/10 text-destructive mb-6 rounded-md border px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <button
        type="button"
        className={cn(
          "border-border bg-card hover:border-accent mb-5 flex w-full flex-col items-center rounded-xl border-[1.5px] border-dashed px-6 py-8 text-center transition-colors",
          drag && "border-accent bg-accent-light/20",
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <Upload className="text-accent h-8 w-8" />
        <p className="mt-2 text-sm">
          <strong>Drop files here</strong> or click to browse
        </p>
        <p className="text-muted mt-1 font-mono text-xs">
          PDF · DOCX · PPTX · XLSX, up to 6 files, 10 MB each
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.pptx,.xlsx,.odt,.odp"
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </button>

      {files.length > 0 && (
        <ul className="mb-5 space-y-2">
          {files.map((file, i) => (
            <li
              key={`${file.name}-${i}`}
              className="text-foreground flex items-center gap-2 text-sm"
            >
              <FileIcon className="text-muted h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{file.name}</span>
              <span className="text-muted font-mono text-xs">
                {Math.round(file.size / 1024)} KB
              </span>
              <button
                type="button"
                className="text-accent text-xs font-medium hover:underline"
                onClick={() => removeFile(i)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <Field
        label="Or paste context"
        hint="Anything that describes the business: strategy notes, a positioning statement, meeting notes."
      >
        <Textarea
          className="bg-card min-h-24"
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          placeholder="Paste text here…"
        />
      </Field>

      {meta.company_url && (
        <label className="mt-5 flex cursor-pointer items-center gap-2.5">
          <Checkbox
            checked={useWebsite}
            onCheckedChange={(checked) => setUseWebsite(checked === true)}
          />
          <span className="text-sm">Also read our website ({meta.company_url})</span>
        </label>
      )}

      {result && (
        <Card className="border-accent mt-6">
          <CardContent className="pt-6">
            <span className="bg-accent-light/60 text-accent inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase">
              <CircleCheck className="h-3.5 w-3.5" /> Analysis complete
            </span>
            <p className="mt-3 text-sm">{result.summary}</p>
            {result.documents.some((d) => d.error) && (
              <ul className="mt-3 space-y-1">
                {result.documents
                  .filter((d) => d.error)
                  .map((d) => (
                    <li key={d.name} className="text-destructive flex items-center gap-2 text-sm">
                      <TriangleAlert className="h-4 w-4 shrink-0" /> {d.name}: {d.error}
                    </li>
                  ))}
              </ul>
            )}
            {result.url_error && (
              <p className="text-muted mt-2 text-xs">Website: {result.url_error}</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <div className="flex items-center gap-3">
          {hasInput && !result && (
            <Button variant="outline" onClick={analyze} disabled={analyzing}>
              {analyzing ? "Reading…" : "Analyze & prefill"}
            </Button>
          )}
          <Button onClick={onContinue}>
            {suggestionCount > 0
              ? `Continue with ${suggestionCount} prefilled`
              : "Skip and answer manually"}{" "}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
